import { IObservable, Subject } from '~/rage/utils';
import {
	IPhoneContactPresence,
	IPhoneDialog,
	IPhoneMessage,
	PhoneMessageDirection,
} from '~/shared';
import {
	IActiveDialogMessagesState,
	IMessagesService,
} from './messagesServiceTypes';

const mockDialogs: IPhoneDialog[] = [
	{
		id: 1,
		peer: {
			phoneNumber: '5550101',
			name: 'stdian',
		},
		lastMessage: null,
		unreadCount: 2,
		updatedAt: Date.now(),
	},
];

const mockMessages: IPhoneMessage[] = [
	{
		id: 1,
		dialogId: 1,
		text: 'Привет, как дела?',
		createdAt: Date.now() - 5 * 60 * 1000,
		authorPhoneNumber: '5550101',
		direction: PhoneMessageDirection.Incoming,
	},
	{
		id: 2,
		dialogId: 1,
		text: 'Все отлично, го кальян с ёлками?',
		createdAt: Date.now() - 4 * 60 * 1000,
		authorPhoneNumber: '5550000',
		direction: PhoneMessageDirection.Outgoing,
	},
	{
		id: 3,
		dialogId: 1,
		text: 'го!',
		createdAt: Date.now() - 3 * 60 * 1000,
		authorPhoneNumber: '5550001',
		direction: PhoneMessageDirection.Incoming,
	},
];

const initialActiveState: IActiveDialogMessagesState = {
	messages: [],
	loading: false,
};

export class MessagesServiceStub implements IMessagesService {
	private readonly dialogs = new Subject<IPhoneDialog[]>(mockDialogs);
	private readonly activeDialogMessages =
		new Subject<IActiveDialogMessagesState>(initialActiveState);
	private readonly contactsPresence = new Subject<IPhoneContactPresence[]>([
		{
			phoneNumber: '5550101',
			isOnline: true,
		},
	]);

	private currentDialogId: number | null = null;
	private readonly activeDialogId = new Subject<number | null>(null);
	private readonly unreadByDialog = new Map<number, number>(
		mockDialogs.map((d) => [d.id, d.unreadCount ?? 0]),
	);

	getDialogs(): IObservable<IPhoneDialog[]> {
		return this.dialogs;
	}

	setActiveDialogId(dialogId: number | null): void {
		this.currentDialogId = dialogId;
		this.activeDialogId.next(dialogId);

		if (dialogId === null) {
			this.activeDialogMessages.next(initialActiveState);
			return;
		}

		this.unreadByDialog.set(dialogId, 0);
		const dialogs = this.dialogs.getValue();
		this.dialogs.next(
			dialogs.map((d) => (d.id === dialogId ? { ...d, unreadCount: 0 } : d)),
		);

		this.activeDialogMessages.next({
			messages: mockMessages.filter((message) => message.dialogId === dialogId),
			loading: false,
		});
	}

	getActiveDialogMessages(): IObservable<IActiveDialogMessagesState> {
		return this.activeDialogMessages;
	}

	getContactsPresence(): IObservable<IPhoneContactPresence[]> {
		return this.contactsPresence;
	}

	getActiveDialogId(): IObservable<number | null> {
		return this.activeDialogId;
	}

	async sendMessage(dialogId: number, text: string): Promise<void> {
		const current = this.activeDialogMessages.getValue();
		const nextMessage: IPhoneMessage = {
			id: Date.now(),
			dialogId,
			text,
			createdAt: Date.now(),
			authorPhoneNumber: '5550101',
			direction: PhoneMessageDirection.Outgoing,
		};

		const nextState: IActiveDialogMessagesState = {
			...current,
			messages: [...current.messages, nextMessage],
		};

		this.activeDialogMessages.next(nextState);

		const prevUnread = this.unreadByDialog.get(dialogId) ?? 0;
		this.unreadByDialog.set(
			dialogId,
			this.currentDialogId === dialogId ? 0 : prevUnread,
		);
	}

	async createDialog(
		targetPhoneNumber: string,
		message: string,
	): Promise<number> {
		const dialogs = this.dialogs.getValue();
		const newId = dialogs.length + 1;

		const newDialog: IPhoneDialog = {
			id: newId,
			peer: {
				phoneNumber: targetPhoneNumber,
				name: targetPhoneNumber,
			},
			lastMessage: null,
			unreadCount: 0,
			updatedAt: Date.now(),
		};

		this.dialogs.next([...dialogs, newDialog]);

		this.setActiveDialogId(newId);
		await this.sendMessage(newId, message);

		return newId;
	}
}
